"use client"
import React, { ReactNode, useCallback, useEffect, useRef } from "react"
import Script from "next/script"
import { usePathname } from "next/navigation"
import { GTWalsheimPro } from "@/components/CustomFonts"

// Outfame tracking IDs
const GA4_ID = "G-YVQYQP4HWD"
const GOOGLE_ADS_ID = "AW-16655696043"
const META_PIXEL_ID = "753687921113071"
const TIKTOK_PIXEL_ID = "D5UVE1JC77U5QAK8T8OG"

// Stored verbatim under their own name (excluding gclid / ttclid / fbclid: prefixed below).
// utm_campaignid / utm_adgroupid arrive via the account tracking_url_template
// (ValueTrack {campaignid}/{adgroupid}) — capture them for per-campaign payer/LTV truth.
// msclkid / adid: Microsoft Ads click id and ad id, kept raw (no prefix) like gbraid / wbraid.
const VERBATIM_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "utm_campaignid", "utm_adgroupid", "gbraid", "wbraid", "dclid", "msclkid", "adid", "offer"] as const

function readDocCookie(name: string): string | null {
    const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
    if (!m) return null
    try {
        return decodeURIComponent(m[1])
    } catch {
        return m[1]
    }
}

/**
 * Click / utm cookies this landing should write, as [name, value] pairs. The middleware already stamps the same
 * cookies server-side (src/lib/tracking/stamp-click-ctx.ts, 90 days); Safari caps any cookie rewritten by
 * document.cookie at 7 days (24 h after a link-decorated click), so a key is written only when its cookie is missing
 * or holds a different (decoded) value. Pages the middleware skips (blog, alternatives) still get every cookie.
 * Pure: `current` reads the decoded cookie value (null when missing).
 */
export function trackingCookieWrites(params: URLSearchParams, current: (name: string) => string | null, nowIso: string): Array<[string, string]> {
    const out: Array<[string, string]> = []
    const want = (name: string, value: string): boolean => {
        const have = current(name)
        if (have !== null && have.trim() === value.trim()) return false
        out.push([name, value])
        return true
    }
    for (const param of VERBATIM_PARAMS) {
        const value = params.get(param)
        if (value) want(param, value)
    }

    // Handle ref parameter
    const ref = params.get("ref")
    if (ref) want("ref", ref)

    // Handle GCLID specially - store BOTH raw and prefixed versions
    // Raw GCLID is required for Google Ads API compliance
    // Prefixed GCLID is used for source tracking (adweek vs google)
    const gclid = params.get("gclid")
    if (gclid) {
        const newClick = want("gclid_raw", gclid) // Raw for Google Ads API
        // Click landing time — measures stale-cookie attribution; rewritten only with a new click id.
        if (newClick || current("gclid_captured_at") === null) out.push(["gclid_captured_at", nowIso])
        const prefix = ref?.includes("adweek") ? "adweek" : "google"
        want("gclid", `${prefix}_${gclid}`) // Prefixed for source tracking
    }

    // Other ad platform click IDs with source prefix
    const ttclid = params.get("ttclid")
    if (ttclid) want("ttclid", `tiktok_${ttclid}`)
    const fbclid = params.get("fbclid")
    if (fbclid) want("fbclid", `meta_${fbclid}`)
    return out
}

export default function RouterEventWrapper({ children }: { children: ReactNode }) {
    const pathname = usePathname()
    const isAppPath = /^\/(.*\/)?app/.test(pathname)

    // Disable browser scroll restoration — prevents mobile Safari from retaining scroll position
    useEffect(() => {
        if ("scrollRestoration" in window.history) {
            window.history.scrollRestoration = "manual"
        }
    }, [])

    // Scroll to top on every client-side navigation
    useEffect(() => {
        window.scrollTo(0, 0)
    }, [pathname])

    // Handle tracking parameters and store in cookies
    const setCookie = useCallback((n: string, v: string) => {
        const d = new Date()
        d.setTime(d.getTime() + 7776e6) // 90 days
        document.cookie = `${n}=${v}; expires=${d.toUTCString()}; path=/`
    }, [])

    useEffect(() => {
        if (typeof window === "undefined") return
        const params = new URLSearchParams(window.location.search)
        for (const [name, value] of trackingCookieWrites(params, readDocCookie, new Date().toISOString())) setCookie(name, value)
    }, [setCookie])

    // Identify returning visitors from persistent cookies (set by TrackingScripts)
    useEffect(() => {
        if (typeof window === "undefined") return
        if (process.env.NODE_ENV !== "production") return

        const readCookie = (name: string) => {
            const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
            return match ? decodeURIComponent(match[1]) : null
        }

        const email = readCookie("_of_em")
        const uid = readCookie("_of_uid")
        if (!email) return

        // Delay to ensure pixel SDKs have initialized (lazyOnload scripts need more time)
        const timer = setTimeout(() => {
            // TikTok identify
            try {
                const ttq = (window as any).ttq
                if (ttq && typeof ttq.identify === "function") {
                    const data: Record<string, string> = { email }
                    if (uid) data.external_id = uid
                    ttq.identify(data)
                }
            } catch (e) {
                // Non-critical
            }

            // Meta identify (re-init with Advanced Matching data)
            try {
                if ((window as any).fbq) {
                    (window as any).fbq("init", META_PIXEL_ID, { em: email })
                }
            } catch (e) {
                // Non-critical
            }
        }, 3000) // 3s accounts for lazyOnload pixel initialization

        return () => clearTimeout(timer)
    }, []) // Only on mount

    // Track page views on route changes (for SPA navigation)
    const isFirstRender = useRef(true)

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false
            return
        }
        // GA4 pageview
        if (typeof window !== "undefined" && window.gtag) {
            window.gtag("config", GA4_ID, { page_path: pathname })
        }
        // Meta Pixel PageView on route change
        if (typeof window !== "undefined" && (window as any).fbq) {
            try {
                (window as any).fbq("track", "PageView")
            } catch (e) {
                // Ignore Meta Pixel errors
            }
        }
        // TikTok Pixel PageView on route change
        if (typeof window !== "undefined") {
            try {
                const ttq = (window as any).ttq
                if (ttq && typeof ttq.page === "function") {
                    ttq.page()
                }
            } catch (e) {
                // Ignore TikTok Pixel errors - SDK may be in partial state during navigation
            }
        }
    }, [pathname])

    return (
        <>
            <div className={`${!isAppPath && GTWalsheimPro.className}`}>{children}</div>

            {/* Google Analytics 4 + Google Ads - Outfame */}
            {process.env.NODE_ENV === "production" && (
                <>
                    <Script
                        src={`https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`}
                        strategy="lazyOnload"
                    />
                    <Script id="gtag-init" strategy="lazyOnload">
                        {`
                            window.dataLayer = window.dataLayer || [];
                            function gtag(){dataLayer.push(arguments);}
                            gtag('js', new Date());
                            gtag('config', '${GA4_ID}');
                            gtag('config', '${GOOGLE_ADS_ID}', {
                                'allow_enhanced_conversions': true
                            });
                        `}
                    </Script>

                    {/* Meta Pixel */}
                    <Script id="meta-pixel-init" strategy="lazyOnload">
                        {`
                            !function(f,b,e,v,n,t,s)
                            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                            n.queue=[];t=b.createElement(e);t.async=!0;
                            t.src=v;s=b.getElementsByTagName(e)[0];
                            s.parentNode.insertBefore(t,s)}(window, document,'script',
                            'https://connect.facebook.net/en_US/fbevents.js');
                            fbq('init', '${META_PIXEL_ID}');
                            fbq('track', 'PageView');
                        `}
                    </Script>
                    <noscript>
                        <img
                            height="1"
                            width="1"
                            style={{ display: "none" }}
                            src={`https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1`}
                            alt=""
                        />
                    </noscript>

                    {/* TikTok Pixel */}
                    <Script id="tiktok-pixel-init" strategy="lazyOnload">
                        {`
                            !function (w, d, t) {
                                w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq._q=ttq._q||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t._q.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var a=d.createElement("script");a.type="text/javascript",a.async=!0,a.src=r+"?sdkid="+e+"&lib="+t;var s=d.getElementsByTagName("script")[0];s.parentNode.insertBefore(a,s)};
                                ttq.load('${TIKTOK_PIXEL_ID}');
                                ttq.page();
                            }(window, document, 'ttq');
                        `}
                    </Script>
                </>
            )}
        </>
    )
}
